import React, { useState, useEffect } from "react";
import { useRouteMatch } from "react-router-dom";
import { Container } from "reactstrap";
import {
  TabContent,
  Nav,
  Button,
  Breadcrumb,
  BreadcrumbItem,
} from "reactstrap";
import "./index.scss";
import StoreInformationTab from "../../components/StoreInformationTab";
import LocationInformationTab from "../../components/LocationInformationTab";
import ApiCaller from "../../../../api/apiCaller";
import Loading from "../../../../components/Loading";
import ImagesTab from "../../components/ImageTab";
import _ from "lodash";
import LegalInformationTab from "../../components/LegalInfomationTab";
import t from "utils/Lang";
import api from "api";

interface StoreEditProps {
  storeDataProps: any;
  storeIdProps: any;
  financial: any;
}

export default function StoreEdit({
  storeDataProps,
  storeIdProps,
  financial,
}: StoreEditProps) {
  const isView = storeIdProps ? true : false;
  const [financialData, setFinancial] = useState(financial);
  const match = useRouteMatch("/store/:store_id/edit");
  const params: any = match?.params;
  const storeId = isView ? storeIdProps : params?.store_id;

  const [activeTab, setActiveTab] = useState("store_information_tab");

  // Options
  const [statusOptions, setStatusOptions] = useState([]);
  const [customerGroupOptions, setCustomerGroupOptions] = useState([]);
  const [storeFormatOptions, setStoreFormatOptions] = useState([]);
  const [storeTypeOptions, setStoreTypeOptions] = useState([]);
  const [parkingSiteTypeOptions, setParkingSiteTypeOptions] = useState([]);
  const [sitePositionTypeOptions, setSitePositionTypeOptions] = useState([]);
  const [siteSurroundingAreaTypeOptions, setSiteSurroundingAreaTypeOptions] =
    useState([]);

  const [storeData, setStoreData] = useState({
    data: isView ? storeDataProps?.data : null,
    labels: isView ? storeDataProps?.labels : null,
    loading: !isView,
    customAttributes: isView ? storeDataProps.customAttributes : [],
  });
  const [selectedPhotos, setSelectedPhotos] = useState<{
    [k: string]: { [k: string]: any };
  }>({});

  const toggle = (tab: string) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const getStoreDetail = async () => {
    const [
      res,
      statusOptionsRes,
      customerGroupOptionRes,
      storeFormatOptionRes,
      storeTypeOptionRes,
      parkingSiteTypeOptionRes,
      sitePositionTypeOptionRes,
      siteSurroundingAreaTypeRes,
    ] = await Promise.all([
      ApiCaller.get(`store/${storeId}`),
      ApiCaller.get(
        `status/?q=(filters:!((col:content_type,opr:eq,value:store)))`
      ),
      ApiCaller.get(
        "customer_group/?q=(filters:!((col:is_active,opr:eq,value:1)))"
      ),
      ApiCaller.get(
        "store_format/?q=(filters:!((col:is_active,opr:eq,value:1)))"
      ),
      ApiCaller.get(
        "store_type/?q=(filters:!((col:is_active,opr:eq,value:1)))"
      ),
      ApiCaller.get("parking_site_type/"),
      ApiCaller.get(
        "site_position_type/?q=(filters:!((col:is_active,opr:eq,value:1)))"
      ),
      ApiCaller.get("site_surrounding_area_type/"),
    ]);
    if (statusOptionsRes?.result) {
      setStatusOptions(
        statusOptionsRes?.result?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.name,
          };
        })
      );
    }
    if (customerGroupOptionRes?.result) {
      setCustomerGroupOptions(
        customerGroupOptionRes?.result?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.name,
          };
        })
      );
    }
    if (storeFormatOptionRes?.result) {
      setStoreFormatOptions(
        storeFormatOptionRes?.result?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.name,
          };
        })
      );
    }
    if (storeTypeOptionRes?.result) {
      setStoreTypeOptions(
        storeTypeOptionRes?.result?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.name,
          };
        })
      );
    }
    if (parkingSiteTypeOptionRes?.result) {
      setParkingSiteTypeOptions(
        parkingSiteTypeOptionRes?.result?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.name,
          };
        })
      );
    }
    if (sitePositionTypeOptionRes?.result) {
      setSitePositionTypeOptions(
        sitePositionTypeOptionRes?.result?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.name,
          };
        })
      );
    }
    if (siteSurroundingAreaTypeRes?.result) {
      setSiteSurroundingAreaTypeOptions(
        siteSurroundingAreaTypeRes?.result?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.name,
          };
        })
      );
    }
    if (res?.result) {
      const resCustomeAttributes = await ApiCaller.get(
        `location_attribute/?q=(filters:!((col:location_type,opr:rel_o_m,value:${res?.result?.location_type?.id})))`
      );
      if (resCustomeAttributes?.result) {
        setStoreData({
          data: res?.result,
          labels: res?._labels,
          loading: false,
          customAttributes: resCustomeAttributes?.result,
        });
      } else {
        setStoreData({
          data: res?.result,
          labels: res?._labels,
          loading: false,
          customAttributes: [],
        });
      }
    }
  };

  const updateStoreData = async (goToViewPage = false) => {
    setStoreData({ ...storeData, ...{ loading: true } });
    if (storeData.data.contract?.id) {
      ApiCaller.put(`contract/${storeData.data?.contract?.id}`, {
        should_renew: storeData.data.contract?.should_renew,
      });
    }
    const res = await ApiCaller.put(`store/${storeId}`, storeData.data);
    if (res?.result) {
      let promiseAll: Array<any> = [];
      if (Object.keys(selectedPhotos).length > 0) {
        Object.keys(selectedPhotos).forEach(async (photoAttribute: string) => {
          var attachmentFormData = new FormData();
          attachmentFormData.append(
            "user_file",
            selectedPhotos[photoAttribute]["url"]
          );
          attachmentFormData.append(
            "caption_text",
            selectedPhotos[photoAttribute]["caption_text"]
          );
          promiseAll.push(
            ApiCaller.post(
              `storefiles/${storeId}/photo_upload/${photoAttribute}`,
              attachmentFormData
            )
          );
        });
      }
      const resAttachmentData = await Promise.all(promiseAll);
      console.log(resAttachmentData);
    }
    if (goToViewPage) {
      window.location.href = `/store/${storeId}/`;
    }
    setStoreData({ ...storeData, ...{ loading: false } });
  };

  const onImageRemoved = (imageAttribute: string) => {
    if (imageAttribute) {
      delete storeData?.data?.photos[imageAttribute];
      setStoreData({ ...storeData });
    }
  };

  const getFinancialPerformance = async () => {
    const res = await api.getFinancialPerformance(storeId);
    if (res.result) {
      setFinancial(res.result);
    }
  };

  useEffect(() => {
    if (!isView) {
      getStoreDetail();
      getFinancialPerformance();
    }
  }, []);

  if (storeData.loading) return <Loading />;

  const tabData = [
    { label: t().store_detail__store_summary, id: "store_information_tab" },
    { label: t().store_detail__store_information, id: "location_information_tab" },
    { label: t().store_detail__store_photo, id: "image_tab" },
    { label: t().store_detail__store_legal, id: "legal_information_tab" },
  ];

  const body = (
    <>
      <Nav tabs className="store-edit-tab">
        {tabData.map((data) => {
          return (
            <div
              key={data.id}
              className={`normal-button store-edit-tab-item ${
                activeTab === data.id ? "active" : ""
              }`}
              onClick={() => toggle(data.id)}
            >
              <span>{data.label}</span>
            </div>
          );
        })}
      </Nav>
      <TabContent activeTab={activeTab}>
        <StoreInformationTab
          {...{
            isView,
            storeData,
            setStoreData,
            statusOptions,
            customerGroupOptions,
            storeFormatOptions,
            storeTypeOptions,
            parkingSiteTypeOptions,
            financial: financialData,
          }}
        />
        <LocationInformationTab
          {...{
            isView,
            storeData,
            setStoreData,
            sitePositionTypeOptions,
            siteSurroundingAreaTypeOptions,
          }}
        />
        <ImagesTab
          {...{
            isView,
            storeData,
            setStoreData,
            selectedPhotos,
            setSelectedPhotos,
            onImageRemoved,
          }}
        />
        <LegalInformationTab
          {...{
            isView,
            storeData,
            setStoreData,
          }}
        />
      </TabContent>
    </>
  );

  if (isView) {
    return <div>{body}</div>;
  }

  return (
    <Container>
      <div className="store-breadcrumb-view">
        <Breadcrumb>
          <BreadcrumbItem>
            <a href="/store">{t().store_listing__store}</a>
          </BreadcrumbItem>
          <BreadcrumbItem active>{storeData?.data?.name}</BreadcrumbItem>
        </Breadcrumb>
        <div>
          <Button color="primary" onClick={() => updateStoreData(true)}>
            {t().action__save_and_preview}
          </Button>
        </div>
      </div>
      {body}
    </Container>
  );
}
